import BrandMark from "../components/brand/BrandMark";
import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Users, CreditCard, QrCode, LogOut, 
  BarChart3, Search, MoreVertical, Play, Pause, Ban,
  CheckCircle, Clock, XCircle, Settings, CalendarIcon, X,
  Download, Eye, FileText
} from "lucide-react";

const API = '/api';

const AdminVehicles = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Filters
  const [qrStatusFilter, setQrStatusFilter] = useState(searchParams.get('qr_status') || "all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  
  // QR Modal
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);

  useEffect(() => {
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrStatusFilter, paymentStatusFilter, dateFrom, dateTo]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (qrStatusFilter && qrStatusFilter !== "all") {
        params.qr_status = qrStatusFilter;
      }
      if (paymentStatusFilter && paymentStatusFilter !== "all") {
        params.payment_status = paymentStatusFilter;
      }
      if (dateFrom) {
        params.date_from = format(dateFrom, "yyyy-MM-dd");
      }
      if (dateTo) {
        params.date_to = format(dateTo, "yyyy-MM-dd") + "T23:59:59";
      }
      
      const response = await axios.get(`${API}/admin/vehicles`, { params });
      setVehicles(response.data);
    } catch (error) {
      toast.error("Error al cargar vehículos");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (vehicleId, status) => {
    try {
      await axios.put(`${API}/admin/vehicles/${vehicleId}/status?status=${status}`);
      toast.success(`Estado actualizado a ${status}`);
      fetchVehicles();
    } catch (error) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleViewQR = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setQrDialogOpen(true);
    setLoadingQr(true);
    
    try {
      // Fetch QR image from admin endpoint (always regenerates for latest data)
      const response = await axios.get(`${API}/admin/vehicles/${vehicle.id}/qr/png`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setQrImageUrl(url);
    } catch (error) {
      toast.error("Error al cargar QR");
      setQrImageUrl(null);
    } finally {
      setLoadingQr(false);
    }
  };

  const handleDownloadQR = async (vehicleId, format) => {
    try {
      const response = await axios.get(`${API}/admin/vehicles/${vehicleId}/qr/${format}`, {
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avisocar-qr-${selectedVehicle?.name || vehicleId}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`QR descargado en formato ${format.toUpperCase()}`);
    } catch (error) {
      toast.error(`Error al descargar QR ${format.toUpperCase()}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const clearDateFilters = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  const filteredVehicles = vehicles.filter(v => 
    v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.qr_token.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-[#1B84FF]/20 text-[#5BC0FF]"><CheckCircle className="w-3 h-3 mr-1" />Activo</Badge>;
      case "inactive":
        return <Badge className="bg-slate-500/20 text-slate-400"><Clock className="w-3 h-3 mr-1" />Inactivo</Badge>;
      case "suspended":
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" />Suspendido</Badge>;
      default:
        return <Badge className="bg-slate-500/20 text-slate-400">{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-[#0B2545]">
      {/* Sidebar */}


      {/* Mobile Header */}
      

      {/* Main Content */}
      <main className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Control de Vehículos
            </h1>
            <p className="text-slate-400 mt-1">Gestiona los vehículos y códigos QR</p>
          </div>

          {/* Filters */}
          <Card className="bg-[#14375F] border-white/15 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col gap-4">
                {/* Row 1: Search and Status Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por vehículo, dueño o token..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      data-testid="search-vehicles-input"
                    />
                  </div>
                  
                  {/* QR Status Filter */}
                  <Select value={qrStatusFilter} onValueChange={setQrStatusFilter}>
                    <SelectTrigger className="w-full sm:w-44 bg-slate-700 border-slate-600 text-white" data-testid="filter-qr-status">
                      <SelectValue placeholder="Estado QR" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los QR</SelectItem>
                      <SelectItem value="active">QR Activo</SelectItem>
                      <SelectItem value="inactive">QR Inactivo</SelectItem>
                      <SelectItem value="suspended">QR Suspendido</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Payment Status Filter */}
                  <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
                    <SelectTrigger className="w-full sm:w-44 bg-slate-700 border-slate-600 text-white" data-testid="filter-payment-status">
                      <SelectValue placeholder="Estado Pago" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los pagos</SelectItem>
                      <SelectItem value="paid">Pagado</SelectItem>
                      <SelectItem value="pending">Pendiente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Row 2: Date Filters */}
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <span className="text-sm text-slate-400 whitespace-nowrap">Fecha de creación:</span>
                  
                  {/* Date From */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full sm:w-44 justify-start text-left font-normal bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                        data-testid="vehicle-date-from-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, "dd/MM/yyyy") : "Desde"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#14375F] border-white/15" align="start">
                      <Calendar
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        locale={es}
                        className="bg-[#14375F] text-white"
                      />
                    </PopoverContent>
                  </Popover>
                  
                  {/* Date To */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full sm:w-44 justify-start text-left font-normal bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                        data-testid="vehicle-date-to-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, "dd/MM/yyyy") : "Hasta"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#14375F] border-white/15" align="start">
                      <Calendar
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        locale={es}
                        className="bg-[#14375F] text-white"
                      />
                    </PopoverContent>
                  </Popover>
                  
                  {/* Clear Dates */}
                  {(dateFrom || dateTo) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearDateFilters}
                      className="text-slate-400 hover:text-white"
                      data-testid="clear-vehicle-dates-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Limpiar
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vehicles Table */}
          <Card className="bg-[#14375F] border-white/15">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/15 hover:bg-white/10">
                        <TableHead className="text-slate-400 min-w-[120px]">Vehículo</TableHead>
                        <TableHead className="text-slate-400 hidden md:table-cell">Dueño</TableHead>
                        <TableHead className="text-slate-400 hidden lg:table-cell">Token QR</TableHead>
                        <TableHead className="text-slate-400">Estado</TableHead>
                        <TableHead className="text-slate-400 hidden sm:table-cell">Pago</TableHead>
                        <TableHead className="text-slate-400 hidden lg:table-cell">Creado</TableHead>
                        <TableHead className="text-slate-400">QR</TableHead>
                        <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVehicles.map((vehicle) => (
                        <TableRow key={vehicle.id} className="border-white/15 hover:bg-slate-750" data-testid={`vehicle-row-${vehicle.id}`}>
                          <TableCell className="font-semibold text-white text-sm sm:text-base">
                            {vehicle.name}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <div>
                              <p className="text-slate-300 text-sm">{vehicle.user?.full_name}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[150px]">{vehicle.user?.email}</p>
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <code className="text-xs text-sky-400 bg-slate-700 px-2 py-1 rounded">
                              {vehicle.qr_token.substring(0, 8)}…
                            </code>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(vehicle.qr_status)}
                            {/* iter71 4.1 — pausa de avisos (ficha admin) */}
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/admin/vehicles/${vehicle.id}/pause-notices`, { paused: !vehicle.notices_paused });
                                  fetchVehicles();
                                } catch (_e) { /* noop */ }
                              }}
                              className={`mt-1 block text-[10px] px-2 py-0.5 rounded-full border ${vehicle.notices_paused ? "border-amber-400/60 text-amber-300 bg-amber-500/10" : "border-slate-600 text-slate-500 hover:text-slate-300"}`}
                              data-testid={`admin-pause-toggle-${vehicle.id}`}
                            >
                              {vehicle.notices_paused ? "⏸️ En pausa — reanudar" : "⏸️ Pausar avisos"}
                            </button>
                            {/* iter72 1.1 — rotar QR (admin, doble confirmación) */}
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm(`Rotar el QR de "${vehicle.name}": el sticker impreso del cliente dejará de funcionar y tendrá que imprimir el nuevo. ¿Continuar?`)) return;
                                if (!window.confirm("Confirmación final: esta acción es irreversible. ¿Rotar el QR ahora?")) return;
                                try {
                                  const r = await axios.post(`${API}/admin/vehicles/${vehicle.id}/rotate-qr`, { confirm: true });
                                  alert(`QR rotado. Sesiones cerradas: ${r.data?.sessions_closed || 0}`);
                                  fetchVehicles();
                                } catch (_e) { alert("No se pudo rotar el QR"); }
                              }}
                              className="mt-1 block text-[10px] px-2 py-0.5 rounded-full border border-sky-500/50 text-sky-300 bg-sky-500/10 hover:bg-sky-500/20"
                              data-testid={`admin-rotate-qr-${vehicle.id}`}
                            >
                              🔄 Rotar QR
                            </button>
                          </TableCell>
                          <TableCell className="hidden sm:table-cell">
                            {vehicle.payment_status === "approved" ? (
                              <Badge className="bg-[#1B84FF]/20 text-[#5BC0FF] text-xs">Pagado</Badge>
                            ) : vehicle.payment_status === "pending" || vehicle.payment_status === "payment_review" ? (
                              <Badge className="bg-amber-500/20 text-amber-400 text-xs">Pendiente</Badge>
                            ) : (
                              <Badge className="bg-slate-500/20 text-slate-400 text-xs">Sin pago</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-400 hidden lg:table-cell text-sm">
                            {new Date(vehicle.created_at).toLocaleDateString('es-PE')}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewQR(vehicle)}
                              className="text-purple-400 hover:text-purple-300 px-2"
                              data-testid={`view-qr-${vehicle.id}`}
                            >
                              <Eye className="w-4 h-4" />
                              <span className="hidden sm:inline ml-1">Ver QR</span>
                            </Button>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-slate-400" data-testid={`vehicle-actions-${vehicle.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-[#14375F] border-white/15">
                                <DropdownMenuItem 
                                  onClick={() => handleStatusChange(vehicle.id, "active")}
                                  className="text-[#5BC0FF] focus:text-emerald-300 focus:bg-slate-700"
                                  data-testid={`activate-vehicle-${vehicle.id}`}
                                >
                                  <Play className="w-4 h-4 mr-2" />
                                  Activar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleStatusChange(vehicle.id, "inactive")}
                                  className="text-slate-400 focus:text-slate-300 focus:bg-slate-700"
                                  data-testid={`deactivate-vehicle-${vehicle.id}`}
                                >
                                  <Pause className="w-4 h-4 mr-2" />
                                  Desactivar
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleStatusChange(vehicle.id, "suspended")}
                                  className="text-red-400 focus:text-red-300 focus:bg-slate-700"
                                  data-testid={`suspend-vehicle-${vehicle.id}`}
                                >
                                  <Ban className="w-4 h-4 mr-2" />
                                  Suspender
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {!loading && filteredVehicles.length === 0 && (
                <div className="text-center py-12">
                  <QrCode className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No se encontraron vehículos</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Count */}
          {!loading && (
            <p className="text-sm text-slate-500 mt-4 text-right">
              Mostrando {filteredVehicles.length} vehículo(s)
            </p>
          )}
        </div>
      </main>

      {/* QR View Modal */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="bg-[#14375F] border-white/15 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-purple-400" />
              Código QR - {selectedVehicle?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Vehicle Info */}
            <div className="bg-slate-700 p-3 rounded-lg text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-slate-400">Dueño</p>
                  <p className="text-white">{selectedVehicle?.user?.full_name}</p>
                </div>
                <div>
                  <p className="text-slate-400">Estado QR</p>
                  <p className="text-white capitalize">{selectedVehicle?.qr_status}</p>
                </div>
              </div>
            </div>
            
            {/* QR Image */}
            <div className="flex justify-center p-4 bg-white rounded-lg">
              {loadingQr ? (
                <div className="w-48 h-48 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/10"></div>
                </div>
              ) : qrImageUrl ? (
                <img 
                  src={qrImageUrl} 
                  alt={`QR de ${selectedVehicle?.name}`} 
                  className="w-48 h-auto"
                />
              ) : (
                <div className="w-48 h-48 flex items-center justify-center text-slate-400">
                  Error al cargar QR
                </div>
              )}
            </div>
            
            {/* Download Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={() => handleDownloadQR(selectedVehicle?.id, 'png')}
                className="flex-1 bg-[#1B84FF] hover:bg-[#0F6FDB]"
                disabled={!qrImageUrl}
                data-testid="download-qr-png"
              >
                <Download className="w-4 h-4 mr-2" />
                Descargar PNG
              </Button>
              <Button
                onClick={() => handleDownloadQR(selectedVehicle?.id, 'pdf')}
                className="flex-1 bg-sky-500 hover:bg-sky-600"
                disabled={!qrImageUrl}
                data-testid="download-qr-pdf"
              >
                <FileText className="w-4 h-4 mr-2" />
                Descargar PDF
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQrDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVehicles;
